import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiArrowLeft,
  FiArrowRight,
  FiArrowUpRight,
  FiHome,
  FiHeart,
  FiMapPin,
  FiMaximize2,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { MdPets } from 'react-icons/md';
import { useAuth } from '../auth/AuthContext';
import { apiUrl } from '../config/api';
import { prefetchPropertyById } from '../services/propertiesService';
import { getCurrentUserHiddenKey, isPropertyHidden, toggleHiddenProperty } from '../utils/hiddenProperties';

const FALLBACK_IMAGE =
  'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800"%3E%3Crect width="1200" height="800" fill="%23e7e3dc"/%3E%3Cpath d="M475 455V344l125-99 125 99v111H475Z" fill="none" stroke="%237b898c" stroke-width="8"/%3E%3Cpath d="M550 455V366h100v89" fill="none" stroke="%237b898c" stroke-width="8"/%3E%3C/svg%3E';

function getFeatureValue(features, keys) {
  if (!features || Array.isArray(features)) return 0;
  for (const key of keys) {
    const value = Number(features[key]);
    if (Number.isFinite(value) && value > 0) return value;
  }
  return 0;
}

function humanizeLocation(value) {
  return String(value || '').replace(/_/g, ' ').trim();
}

function PropertyCard({ property, onHover, highlighted = false, detailQuery = '' }) {
  const [current, setCurrent] = useState(0);
  const [savingFavorite, setSavingFavorite] = useState(false);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const favoriteIds = user?.favorites || [];
  const userHiddenKey = getCurrentUserHiddenKey(user);
  const isFavorite = favoriteIds.some((favorite) => String(favorite?._id || favorite) === String(property._id));
  const isHidden = isPropertyHidden(property._id, userHiddenKey);

  useEffect(() => {
    setSavingFavorite(false);
  }, [property._id]);

  const images = Array.isArray(property.images) && property.images.length
    ? property.images.filter(Boolean)
    : [FALLBACK_IMAGE];

  const next = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setCurrent((value) => (value + 1) % images.length);
  };

  const previous = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setCurrent((value) => (value === 0 ? images.length - 1 : value - 1));
  };

  const formattedPrice = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: property.currency || 'USD',
    maximumFractionDigits: 0,
  }).format(Number(property.price) || 0);

  const bedrooms = getFeatureValue(property.features, ['dormitorios', 'habitaciones'])
    || Number(property.bedrooms)
    || Number(property.rooms)
    || 0;
  const bathrooms = getFeatureValue(property.features, ['banos', 'baños'])
    || Number(property.bathrooms)
    || 0;
  const surface = getFeatureValue(property.features, ['metrosTotales', 'metrosCubiertos'])
    || Number(property.surfaceCovered)
    || Number(property.surfaceTotal)
    || 0;
  const vacationGuests = Number(property.vacation?.maximumGuests) || 0;
  const vacationBeds = Number(property.vacation?.beds) || 0;
  const sleepingPlaces = Number(property.vacation?.sleepingPlaces) || vacationGuests;

  const address = humanizeLocation(property.address);
  const neighborhood = humanizeLocation(property.neighborhood);
  const department = humanizeLocation(property.department);
  const province = humanizeLocation(property.province);
  const locationContext = [neighborhood, department]
    .filter((item, index, items) => item && items.indexOf(item) === index)
    .join(' · ');
  const locationPrimary = address || neighborhood || department || province || 'Ubicación a confirmar';
  const locationSecondary = address
    ? (locationContext || province)
    : [department, province]
      .filter((item, index, items) => item && item !== locationPrimary && items.indexOf(item) === index)
      .join(' · ');
  const type = property.type?.replace(/_/g, ' ') || 'Propiedad';
  const title = property.title || `${type} en ${locationPrimary}`;
  const detailParams = new URLSearchParams(detailQuery);
  if (property.editorialCampaignId) { detailParams.set('campaign', property.editorialCampaignId); detailParams.set('source','EDITORIAL'); }
  else if (property.isPromoted) detailParams.set('source','FEATURED');
  else if (!detailParams.has('source')) detailParams.set('source','SEARCH');
  const detailSearch = detailParams.toString();
  const operationLabel = property.operation === 'alquiler_vacacional'
    ? 'VACACIONAL'
    : property.operation === 'alquiler_temporal'
      ? 'TEMPORARIO'
      : property.operation?.replaceAll('_', ' ').toUpperCase() || 'VENTA';
  const prefetchDetail = () => prefetchPropertyById(property._id);
  const toggleFavorite = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (savingFavorite) return;
    if (!user) {
      try { sessionStorage.setItem('nido:pending-favorite', String(property._id)); } catch { /* El login sigue disponible aunque el navegador bloquee storage. */ }
      navigate('/login', { state: { from: { pathname: routerLocation.pathname, search: routerLocation.search } } });
      return;
    }

    setSavingFavorite(true);
    try {
      const response = await fetch(apiUrl(`/users/favorites/${property._id}`), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.message || 'No pudimos actualizar tus favoritos');
      setUser((currentUser) => currentUser ? { ...currentUser, favorites: data.favorites || [] } : currentUser);
    } catch {
      // La tarjeta conserva el estado anterior si la actualización falla.
    } finally {
      setSavingFavorite(false);
    }
  };

  const dismissProperty = (event) => {
    event.preventDefault();
    event.stopPropagation();
    toggleHiddenProperty(property._id, userHiddenKey);
    window.dispatchEvent(new CustomEvent('nido:hidden-property-updated', { detail: { propertyId: String(property._id), userId: userHiddenKey } }));
  };

  if (isHidden) return null;

  return (
    <CardLink
      to={`/properties/${property._id}${detailSearch ? `?${detailSearch}` : ''}`}
      aria-label={`Ver ${title}`}
      onMouseEnter={() => { onHover?.(property._id); prefetchDetail(); }}
      onMouseLeave={() => onHover?.(null)}
      onFocus={() => { onHover?.(property._id); prefetchDetail(); }}
      onBlur={() => onHover?.(null)}
      onPointerDown={prefetchDetail}
      onTouchStart={prefetchDetail}
    >
      <Card $highlighted={highlighted}>
        <ImageWrapper>
          <Image
            src={images[current] || FALLBACK_IMAGE}
            alt={title}
            loading="lazy"
            onError={(event) => { event.currentTarget.src = FALLBACK_IMAGE; }}
          />
          <ImageShade />

          <TopLine>
            <OperationBadge>{operationLabel}</OperationBadge>
            {property.isDemo && <DemoBadge>DEMO NIDO</DemoBadge>}
            {property.status && property.status !== 'disponible' && (
              <StatusBadge $status={property.status}>{property.status.toUpperCase()}</StatusBadge>
            )}
          </TopLine>

          {property.video?.url && <VideoBadge>VIDEO</VideoBadge>}
          {property.showPriceReduction !== false && Number(property.priceReductionPercentage) > 0 && (
            <ReducedBadge>BAJÓ {Number(property.priceReductionPercentage).toLocaleString('es-AR')}%</ReducedBadge>
          )}

          {images.length > 1 && (
            <GalleryControls>
              <GalleryButton type="button" onClick={previous} aria-label="Foto anterior">
                <FiArrowLeft />
              </GalleryButton>
              <ImageCount>{current + 1} / {images.length}</ImageCount>
              <GalleryButton type="button" onClick={next} aria-label="Foto siguiente">
                <FiArrowRight />
              </GalleryButton>
            </GalleryControls>
          )}
        </ImageWrapper>

        <Content>
          <MetaLine>
            <PropertyType>{type}{property.isPromoted && !property.isDemo && <PromotionNote>Publicación promocionada</PromotionNote>}</PropertyType>
            <MetaActions>
              {property.operation === 'alquiler_vacacional' && (property.vacation?.petsAllowed || property.petsAllowed) && <PetFriendly><MdPets /> Acepta mascotas</PetFriendly>}
              <ActionButtons>
                <FavoriteButton
                  type="button"
                  onClick={toggleFavorite}
                  onPointerDown={(event) => event.stopPropagation()}
                  $active={isFavorite}
                  disabled={savingFavorite}
                  aria-label={isFavorite ? 'Quitar de favoritos' : 'Guardar en favoritos'}
                  aria-pressed={isFavorite}
                >
                  <FiHeart fill={isFavorite ? 'currentColor' : 'none'} />
                </FavoriteButton>
                <HideButton
                  type="button"
                  onClick={dismissProperty}
                  onPointerDown={(event) => event.stopPropagation()}
                  aria-label="No me interesa esta propiedad"
                  title="No me interesa esta propiedad"
                >
                  <FiX />
                </HideButton>
                <OpenIcon aria-hidden="true"><FiArrowUpRight /></OpenIcon>
              </ActionButtons>
            </MetaActions>
          </MetaLine>
          <Title>{title}</Title>
          <Location>
            <FiMapPin aria-hidden="true" />
            <span>
              <LocationPrimary>{locationPrimary}</LocationPrimary>
              {locationSecondary && <LocationSecondary>{locationSecondary}</LocationSecondary>}
            </span>
          </Location>

          <BottomLine>
            <PriceBlock>
              <Price>{formattedPrice}{property.operation === 'alquiler_vacacional' && <small> / noche</small>}{['alquiler','alquiler_temporal'].includes(property.operation) && <small> / mes</small>}</Price>
            </PriceBlock>
            <Details aria-label="Características principales">
              {property.operation === 'alquiler_vacacional' ? <>
                {vacationGuests > 0 && <Detail title="Huéspedes"><FiUsers /> {vacationGuests} huéspedes</Detail>}
                {bedrooms > 0 && <Detail title="Dormitorios"><FiHome /> {bedrooms} dorm.</Detail>}
                {vacationBeds > 0 && <Detail title="Camas">{vacationBeds} camas · {sleepingPlaces} plazas</Detail>}
                {bathrooms > 0 && <Detail title="Baños">{bathrooms} baños</Detail>}
              </> : <>
                {bedrooms > 0 && <Detail title="Dormitorios"><FiHome /> {bedrooms} dorm.</Detail>}
                {bathrooms > 0 && <Detail title="Baños">{bathrooms} {bathrooms === 1 ? 'baño' : 'baños'}</Detail>}
                {surface > 0 && <Detail title="Superficie"><FiMaximize2 /> {surface} m²</Detail>}
              </>}
            </Details>
          </BottomLine>
        </Content>
      </Card>
    </CardLink>
  );
}

export default PropertyCard;

const CardLink = styled(Link)`
  display: block;
  min-width: 0;
  height: 100%;
  color: inherit;
  text-decoration: none;
`;

const Card = styled.article`
  height: 100%;
  overflow: hidden;
  background: #fff;
  border: 1px solid rgba(42, 64, 70, 0.12);
  border-radius: 14px;
  box-shadow: 0 12px 34px rgba(42, 64, 70, 0.055);
  cursor: pointer;
  transition: transform 420ms cubic-bezier(.2,.75,.25,1),
    border-color 250ms ease, box-shadow 420ms ease;

  ${CardLink}:hover & {
    transform: translateY(-6px);
    border-color: rgba(42, 64, 70, 0.24);
    box-shadow: 0 28px 64px rgba(42, 64, 70, 0.13);
  }

  ${({ $highlighted }) => $highlighted && `
    transform: translateY(-6px);
    border-color: rgba(42, 64, 70, 0.24);
    box-shadow: 0 28px 64px rgba(42, 64, 70, 0.13);
  `}

  ${CardLink}:focus-visible & {
    outline: 3px solid rgba(201, 106, 70, 0.35);
    outline-offset: 4px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  height: clamp(220px, 23vw, 310px);
  overflow: hidden;

  @media (max-width: 700px) {
    height: 245px;
  }

  @media (max-width: 360px) {
    height: 220px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 800ms cubic-bezier(.2,.75,.25,1);

  ${CardLink}:hover & {
    transform: scale(1.045);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ImageShade = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(18, 30, 33, 0.2) 0%, transparent 32%, rgba(18, 30, 33, 0.28) 100%);
`;

const TopLine = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 6px 11px;
  border-radius: 999px;
  font-size:12px;
  font-weight: 700;
  letter-spacing: .12em;
  line-height: 1;
  backdrop-filter: blur(10px);
  @media(max-width:620px){
    min-height:24px;
    max-width:calc(100% - 24px);
    padding:5px 8px;
    overflow:hidden;
    font-size:12px;
    letter-spacing:.08em;
    white-space:nowrap;
    text-overflow:ellipsis;
  }
`;

const OperationBadge = styled(Pill)`
  color: #293d42;
  background: rgba(250, 248, 243, 0.94);
`;
const DemoBadge = styled(Pill)`color:#fff;background:rgba(41,71,79,.9);`;

const StatusBadge = styled(Pill)`
  color: white;
  background: ${({ $status }) => $status === 'reservado' ? '#b46042' : '#29474f'};
`;

const VideoBadge = styled(Pill)`
  position:absolute;left:16px;top:58px;color:#fff;background:rgba(41,71,79,.9);
`;
const PromotionNote = styled.small`
  display:inline-flex;margin-left:8px;padding-left:8px;border-left:1px solid rgba(42,64,70,.2);
  color:#7b8587;font-size:11px;font-weight:650;letter-spacing:.02em;text-transform:none;
`;
const ReducedBadge = styled(Pill)`
  position: absolute;
  right: 16px;
  bottom: 16px;
  color: #fff;
  background: #315f50;
  @media(max-width:620px){right:12px;bottom:12px;}
`;

const PriceBlock = styled.div`display: grid; gap: 3px;`;
const GalleryControls = styled.div`
  position: absolute;
  right: 14px;
  bottom: 14px;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 3px;
  color: #fff;
  background: rgba(26, 42, 46, 0.72);
  border: 1px solid rgba(255,255,255,.22);
  border-radius: 999px;
  backdrop-filter: blur(12px);
`;

const GalleryButton = styled.button`
  display: grid;
  width: 32px;
  height: 32px;
  place-items: center;
  padding: 0;
  color: inherit;
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  transition: color 180ms ease, background 180ms ease;

  &:hover, &:focus-visible {
    color: #29474f;
    background: #fff;
    outline: none;
  }
`;

const ImageCount = styled.span`
  min-width: 38px;
  font-size:12px;
  font-variant-numeric: tabular-nums;
  text-align: center;
`;

const Content = styled.div`
  padding: 20px 22px 18px;

  @media (max-width: 700px) {
    padding: 16px 16px 15px;
  }
`;

const MetaLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
`;

const PropertyType = styled.span`
  color: #b45f40;
  font-size:12px;
  font-weight: 700;
  letter-spacing: .13em;
  text-transform: uppercase;
`;
const MetaActions = styled.span`display:flex;align-items:center;gap:8px;`;
const PetFriendly = styled.span`display:inline-flex;align-items:center;gap:5px;color:#426852;background:#edf4ee;border-radius:999px;padding:6px 9px;font-size:12px;font-weight:720;white-space:nowrap;svg{font-size:13px;}@media(max-width:380px){font-size:0;padding:7px;svg{font-size:14px;}}`;
const ActionButtons = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  background: #f5f3ee;
  border: 1px solid rgba(42, 64, 70, .1);
  border-radius: 999px;
`;

const FavoriteButton = styled.button`
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  padding: 0;
  color: ${({ $active }) => $active ? '#fff' : '#29474f'};
  background: ${({ $active }) => $active ? '#c96f4e' : 'transparent'};
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  transition: color 180ms ease, background 180ms ease, border-color 180ms ease, transform 180ms ease;

  svg { font-size: 16px; }
  &:hover, &:focus-visible { color: ${({ $active }) => $active ? '#fff' : '#b45f40'}; background: ${({ $active }) => $active ? '#b85f40' : '#fff'}; transform: translateY(-1px); outline: none; }
  &:focus-visible { box-shadow: 0 0 0 3px rgba(201, 111, 78, .2); }
  &:disabled { cursor: wait; opacity: .62; }
`;

const HideButton = styled.button`
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  padding: 0;
  color: #5f6d72;
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  transition: color 180ms ease, background 180ms ease, transform 180ms ease;

  svg { font-size: 16px; }
  &:hover, &:focus-visible {
    color: #b45f40;
    background: #fff;
    transform: translateY(-1px);
    outline: none;
  }
`;

const OpenIcon = styled.span`
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  color: #29474f;
  border: 0;
  border-radius: 50%;
  transition: color 250ms ease, background 250ms ease, transform 350ms ease;

  ${CardLink}:hover & {
    color: #fff;
    background: #29474f;
    transform: rotate(45deg);
  }
`;

const Title = styled.h3`
  display: -webkit-box;
  min-height: 2.55em;
  margin: 0 0 6px;
  overflow: hidden;
  color: #21373c;
  font-family: 'Manrope', sans-serif;
  font-size: clamp(18px, 1.45vw, 22px);
  font-weight: 580;
  line-height: 1.27;
  letter-spacing: -.025em;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;

  @media (max-width: 700px) {
    min-height: auto;
    font-size: 19px;
  }
`;

const Location = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  min-height: 20px;
  color: #738084;
  font-size: 13px;
  line-height: 1.4;

  svg { flex: 0 0 auto; margin-top: 2px; }
  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
const LocationPrimary = styled.span`
  display: block;
  overflow: hidden;
  color: #5e6e72;
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const LocationSecondary = styled.span`
  display: block;
  overflow: hidden;
  margin-top: 2px;
  color: #8b9698;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BottomLine = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-top: 13px;
  padding-top: 14px;
  border-top: 1px solid rgba(42, 64, 70, .11);

  @media (max-width: 700px) {
    align-items: flex-end;
    gap: 10px;
    margin-top: 11px;
    padding-top: 12px;
  }

  @media (max-width: 480px) {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }
`;

const Price = styled.div`
  color: #172d32;
  font-size: clamp(20px, 1.55vw, 24px);
  font-weight: 720;
  line-height: 1;
  letter-spacing: -.035em;
  white-space: nowrap;
  small{font-size:12px;font-weight:600;color:#6f7f82;letter-spacing:0;}
`;

const Details = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px 12px;

  @media (max-width: 480px) {
    justify-content: flex-start;
    gap: 7px 11px;
  }
`;

const Detail = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #667579;
  font-size: 12px;
  white-space: nowrap;

  svg { font-size: 14px; }
`;
